// src/components/maintenance/components/ValidationView.js
import React, { useState } from 'react';
import { useRecordContext, useNotify, useRefresh } from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  FormControlLabel,
  Switch,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { supabaseClient } from '../../supabase';
import { processMaintenanceOperation } from '../utils/maintenanceProcessor';

const ValidationView = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [inviteNewUsers, setInviteNewUsers] = useState(true);
  const [deleteStudentsNotInCSV, setDeleteStudentsNotInCSV] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [conflictResolutions, setConflictResolutions] = useState({});

  const previewData = record.preview_data || {};
  const hasConflicts = (previewData.householdConflicts || []).length > 0;
  const allConflictsResolved = hasConflicts
    ? Object.keys(conflictResolutions).length === previewData.householdConflicts.length
    : true;

  const handleConflictResolution = (conflictIndex, resolution) => {
    setConflictResolutions(prev => ({
      ...prev,
      [conflictIndex]: resolution,
    }));
  };

  const handleProcess = async () => {
    if (!allConflictsResolved) {
      notify('Please resolve all conflicts before processing', { type: 'warning' });
      return;
    }

    try {
      setProcessing(true);

      // Save conflict resolutions and update status to validated
      const { error: updateError } = await supabaseClient
        .from('maintenance_operations')
        .update({
          status: 'validated',
          conflict_resolutions: conflictResolutions,
          invite_new_users: inviteNewUsers,
          delete_students_not_in_csv: deleteStudentsNotInCSV,  // NEW
        })
        .eq('id', record.id);

      if (updateError) throw updateError;

      // Process the maintenance operation
      await processMaintenanceOperation(record.id);

      notify('Changes processed successfully!', { type: 'success' });
      refresh();

    } catch (error) {
      console.error('Processing error:', error);
      notify(`Error processing changes: ${error.message}`, { type: 'error' });
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box>
      {/* Summary Alert */}
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Processing Summary:</strong><br />
          • Teachers: {previewData.teachers?.new.length || 0} new, {previewData.teachers?.updated.length || 0} updated, {previewData.teachers?.removed.length || 0} removed<br />
          • Classes: {previewData.classes?.new.length || 0} new, {previewData.classes?.updated.length || 0} updated, {previewData.classes?.removed.length || 0} removed<br />
          • Students: {previewData.students?.new.length || 0} new, {previewData.students?.updated.length || 0} updated, {previewData.students?.graduating.length || 0} graduating
        </Typography>
      </Alert>

      {/* Students Without Parents Warning - Grades 0-6 only */}
      {(previewData.studentsWithoutParents || []).length > 0 && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>⚠️ {previewData.studentsWithoutParents.length} student(s) in grades 0-6 have missing parent data!</strong>
          </Typography>
          <Typography variant="body2">
            These students are in the CSV but have no parent information. They will be skipped during processing.
            Please add parent data for these students or remove them from the CSV.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" component="div">
              Affected students (first 10):
            </Typography>
            {previewData.studentsWithoutParents.slice(0, 10).map((s, idx) => (
              <Typography key={idx} variant="caption" component="div">
                • {s.first_name} {s.last_name} (Grade {s.grade}, {s.class_code})
              </Typography>
            ))}
            {previewData.studentsWithoutParents.length > 10 && (
              <Typography variant="caption" color="text.secondary">
                ...and {previewData.studentsWithoutParents.length - 10} more
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {/* Students Not In CSV Warning - Grades 0-6 */}
      {(previewData.studentsToDelete || []).length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>⚠️ {previewData.studentsToDelete.length} student(s) in grades 0-6 are not in the CSV!</strong>
          </Typography>
          <Typography variant="body2">
            These students exist in the database but are not in the uploaded CSV. 
            Use the toggle below to decide whether to delete them or keep them.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" component="div">
              Affected students (first 10):
            </Typography>
            {previewData.studentsToDelete.slice(0, 10).map((s, idx) => (
              <Typography key={idx} variant="caption" component="div">
                • {s.first_name} {s.last_name} (Grade {s.grade}, {s.current_class})
              </Typography>
            ))}
            {previewData.studentsToDelete.length > 10 && (
              <Typography variant="caption" color="text.secondary">
                ...and {previewData.studentsToDelete.length - 10} more
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      {/* Teachers Section */}
      <Accordion defaultExpanded={(previewData.teachers?.new.length > 0 || previewData.teachers?.updated.length > 0 || previewData.teachers?.removed.length > 0)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Teachers
            <Chip
              label={`${previewData.teachers?.new.length || 0} new, ${previewData.teachers?.updated.length || 0} updated, ${previewData.teachers?.removed.length || 0} removed`}
              size="small"
              sx={{ ml: 2 }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* New Teachers */}
          {(previewData.teachers?.new || []).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>New Teachers:</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Mobile</TableCell>
                      <TableCell>Class</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.teachers.new.map((teacher, index) => (
                      <TableRow key={index}>
                        <TableCell>{teacher.first_name} {teacher.last_name}</TableCell>
                        <TableCell>{teacher.email}</TableCell>
                        <TableCell>{teacher.mobile}</TableCell>
                        <TableCell>{teacher.class_code}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Updated Teachers */}
          {(previewData.teachers?.updated || []).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Updated Teachers:</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.teachers.updated.map((teacher, index) => (
                      <TableRow key={index}>
                        <TableCell>{teacher.first_name} {teacher.last_name}</TableCell>
                        <TableCell>
                          {teacher.changes.map((change, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {change.field}:
                              </Typography>
                              <Chip
                                label={change.old || '(empty)'}
                                size="small"
                                sx={{ mx: 0.5, height: 20 }}
                              />
                              <ArrowForwardIcon fontSize="small" />
                              <Chip
                                label={change.new}
                                size="small"
                                color="primary"
                                sx={{ mx: 0.5, height: 20 }}
                              />
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Removed Teachers */}
          {(previewData.teachers?.removed || []).length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Removed Teachers:</Typography>
              <Alert severity="warning">
                The following teachers will be removed:
                {previewData.teachers.removed.map((teacher, index) => (
                  <Typography key={index} variant="body2">
                    • {teacher.first_name} {teacher.last_name} ({teacher.email})
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}

          {(previewData.teachers?.new || []).length === 0 &&
           (previewData.teachers?.updated || []).length === 0 &&
           (previewData.teachers?.removed || []).length === 0 && (
            <Typography color="text.secondary">No teacher changes</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Classes Section */}
      <Accordion defaultExpanded={(previewData.classes?.new.length > 0 || previewData.classes?.updated.length > 0 || previewData.classes?.removed.length > 0)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Classes
            <Chip
              label={`${previewData.classes?.new.length || 0} new, ${previewData.classes?.updated.length || 0} updated, ${previewData.classes?.removed.length || 0} removed`}
              size="small"
              sx={{ ml: 2 }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {/* New Classes */}
          {(previewData.classes?.new || []).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>New Classes:</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Class Code</TableCell>
                      <TableCell>Grade</TableCell>
                      <TableCell>Teacher</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.classes.new.map((cls, index) => (
                      <TableRow key={index}>
                        <TableCell>{cls.class_code}</TableCell>
                        <TableCell>{cls.grade}</TableCell>
                        <TableCell>{cls.teacher_email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Updated Classes */}
          {(previewData.classes?.updated || []).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>Updated Classes:</Typography>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Class Code</TableCell>
                      <TableCell>Changes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.classes.updated.map((cls, index) => (
                      <TableRow key={index}>
                        <TableCell>{cls.class_code}</TableCell>
                        <TableCell>
                          {cls.changes.map((change, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                {change.field}:
                              </Typography>
                              <Chip
                                label={change.old ?? '(empty)'}
                                size="small"
                                sx={{ mx: 0.5, height: 20 }}
                              />
                              <ArrowForwardIcon fontSize="small" />
                              <Chip
                                label={change.new ?? '(empty)'}
                                size="small"
                                color="primary"
                                sx={{ mx: 0.5, height: 20 }}
                              />
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Removed Classes */}
          {(previewData.classes?.removed || []).length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>Removed Classes:</Typography>
              <Alert severity="warning">
                The following classes will be removed:
                {previewData.classes.removed.map((cls, index) => (
                  <Typography key={index} variant="body2">
                    • {cls.class_code} (Grade {cls.grade})
                  </Typography>
                ))}
              </Alert>
            </Box>
          )}

          {(previewData.classes?.new || []).length === 0 &&
           (previewData.classes?.updated || []).length === 0 &&
           (previewData.classes?.removed || []).length === 0 && (
            <Typography color="text.secondary">No class changes</Typography>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Graduating Students Alert */}
      {(previewData.students?.graduating || []).length > 0 && (
        <Alert severity="info" sx={{ my: 2 }}>
          <Typography variant="body2">
            <strong>{previewData.students.graduating.length}</strong> current grade 7 student(s) will be moved to
            the <strong>{record.alumni_class_name}</strong> class.
          </Typography>
        </Alert>
      )}

      {/* New Students Section */}
      <Accordion defaultExpanded={(previewData.students?.new || []).length > 0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            New Students
            <Chip
              label={previewData.students?.new.length || 0}
              size="small"
              color="success"
              sx={{ ml: 2 }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {(previewData.students?.new || []).length === 0 ? (
            <Typography color="text.secondary">No new students</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Class</TableCell>
                    <TableCell>Household</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.students.new.map((student, index) => (
                    <TableRow key={index}>
                      <TableCell>{student.first_name}</TableCell>
                      <TableCell>{student.last_name}</TableCell>
                      <TableCell>{student.class_code}</TableCell>
                      <TableCell>
                        {student.isNewHousehold ? (
                          <Chip label="New Household" size="small" color="success" />
                        ) : (
                          <Chip label="Existing Household" size="small" color="default" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Updated Students Section */}
      <Accordion defaultExpanded={(previewData.students?.updated || []).length > 0}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            Updated Students
            <Chip
              label={previewData.students?.updated.length || 0}
              size="small"
              color="info"
              sx={{ ml: 2 }}
            />
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {(previewData.students?.updated || []).length === 0 ? (
            <Typography color="text.secondary">No updated students</Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Changes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.students.updated.map((student, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        {student.first_name} {student.last_name}
                      </TableCell>
                      <TableCell>
                        {student.changes.map((change, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              {change.field}:
                            </Typography>
                            <Chip
                              label={change.old || '(empty)'}
                              size="small"
                              sx={{ mx: 0.5, height: 20 }}
                            />
                            <ArrowForwardIcon fontSize="small" />
                            <Chip
                              label={change.new}
                              size="small"
                              color="primary"
                              sx={{ mx: 0.5, height: 20 }}
                            />
                          </Box>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Graduating Students Section */}
      {(previewData.students?.graduating || []).length > 0 && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Graduating Students
              <Chip
                label={previewData.students.graduating.length}
                size="small"
                color="primary"
                sx={{ ml: 2 }}
              />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>First Name</TableCell>
                    <TableCell>Last Name</TableCell>
                    <TableCell>Current Class</TableCell>
                    <TableCell>Moving To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.students.graduating.map((student, index) => (
                    <TableRow key={index}>
                      <TableCell>{student.first_name}</TableCell>
                      <TableCell>{student.last_name}</TableCell>
                      <TableCell>{student.current_class}</TableCell>
                      <TableCell>
                        <Chip
                          label={student.alumni_class}
                          size="small"
                          color="primary"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>
      )}

      {/* Students Without Parents Warning */}
      {(previewData.studentsWithoutParents || []).length > 0 && (
      <Alert severity="error" sx={{ my: 2 }}>
          <Typography variant="body2" gutterBottom>
          <strong>⚠️ {previewData.studentsWithoutParents.length} student(s) in the CSV have missing parent data!</strong>
          </Typography>
          <Typography variant="body2">
          These students are in the CSV but have no parent information. They will be skipped during processing.
          Please add parent data for these students or remove them from the CSV if they should graduate.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
          Affected students: {previewData.studentsWithoutParents.slice(0, 5).map(s => 
              `${s.first_name} ${s.last_name}`
          ).join(', ')}
          {previewData.studentsWithoutParents.length > 5 && ` and ${previewData.studentsWithoutParents.length - 5} more...`}
          </Typography>
      </Alert>
      )}

      {/* Household Conflicts Section */}
      {hasConflicts && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6" color="warning.main">
              Household Conflicts
              <Chip
                label={previewData.householdConflicts.length}
                size="small"
                color="warning"
                sx={{ ml: 2 }}
              />
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Alert severity="warning" sx={{ mb: 2 }}>
              The following students have changed parent information.
              Please decide how to handle each case.
            </Alert>

            {previewData.householdConflicts.map((conflict, index) => (
              <Card key={index} sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    {conflict.student.first_name} {conflict.student.last_name}
                  </Typography>

                  {conflict.siblings.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      This student has {conflict.siblings.length} sibling(s) in the same household
                    </Alert>
                  )}

                  <FormControl component="fieldset">
                    <FormLabel component="legend">How should this be handled?</FormLabel>
                    <RadioGroup
                      value={conflictResolutions[index] || ''}
                      onChange={(e) => handleConflictResolution(index, e.target.value)}
                    >
                      <FormControlLabel
                        value="create_new"
                        control={<Radio />}
                        label="Create new household (break from siblings)"
                      />
                      <FormControlLabel
                        value="update_existing"
                        control={<Radio />}
                        label="Update existing household (affects all siblings)"
                      />
                      <FormControlLabel
                        value="keep_existing"
                        control={<Radio />}
                        label="Keep existing household (ignore parent changes)"
                      />
                    </RadioGroup>
                  </FormControl>
                </CardContent>
              </Card>
            ))}
          </AccordionDetails>
        </Accordion>
      )}

      {/* Actions */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={inviteNewUsers}
                onChange={(e) => setInviteNewUsers(e.target.checked)}
              />
            }
            label="Invite new users (send email invitations to new parents)"
          />

          {/* NEW: Delete students toggle */}
          {(previewData.studentsToDelete || []).length > 0 && (
            <FormControlLabel
              control={
                <Switch
                  checked={deleteStudentsNotInCSV}
                  onChange={(e) => setDeleteStudentsNotInCSV(e.target.checked)}
                  color="error"
                />
              }
              label={`Delete ${previewData.studentsToDelete.length} student(s) not in CSV (grades 0-6)`}
            />
          )}

          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckIcon />}
              onClick={handleProcess}
              disabled={processing || !allConflictsResolved}
              fullWidth
              size="large"
            >
              {processing ? 'Processing...' : 'Process Changes'}
            </Button>

            {!allConflictsResolved && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Please resolve all household conflicts before processing.
              </Alert>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ValidationView;